// Named cases the staging review called out by claim number (rcm-26 "Fix on
// staging", Oct 18). Each is built by the same claimBuilder as the rest, with
// the state and the details that make the case: a draft waiting for review, a
// claim that is late only after the payer's usual time, a denial with missing
// information (corrected claim), a denial that fits an appeal, a short payment
// ELVA asked to reprocess, a write-off after a lost appeal, and the six claims
// where the payer pays the patient.
import { buildClaim, proc, DENIALS } from './claimBuilder';
import type { HistoryClaim, TimelineEntry } from './historyMock';
import { PAYS_PATIENT_OPEN } from './paysPatientClaims';

/** Adds one event right after the first one on the same day, 11 minutes later. */
function withDraftEvent(c: HistoryClaim, lead: string, detail: string): HistoryClaim {
  const first = c.timeline[0];
  if (!first) return c;
  const m = /^(.*), (\d{1,2}):(\d{2}) (AM|PM)$/.exec(first.stamp);
  if (!m) return c;
  let h = Number(m[2]) % 12 + (m[4] === 'PM' ? 12 : 0);
  let min = Number(m[3]) + 11;
  if (min >= 60) {
    min -= 60;
    h += 1;
  }
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const entry: TimelineEntry = {
    stamp: `${m[1] ?? ''}, ${String(h12)}:${String(min).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`,
    ...(first.iso ? { iso: first.iso } : {}),
    ok: true,
    lead,
    detail,
    who: 'ELVA',
  };
  return { ...c, timeline: [first, entry, ...c.timeline.slice(1)] };
}

const denialNecessity = DENIALS.find((d) => d.carc === 'CARC 50') ?? DENIALS[0];
const denialFrequency = DENIALS.find((d) => d.carc === 'CARC 119') ?? DENIALS[1];

export const SPECIAL_CLAIMS: HistoryClaim[] = [
  // Waiting for review: documents say "Claim draft"; the draft says what ELVA changed.
  withDraftEvent(
    buildClaim({
      id: 'CLM-28261',
      state: 'waiting',
      patient: 'Theo Lee',
      payer: 'Delta Dental',
      plan: 'Delta Dental PPO',
      procedures: [proc('D2740', '#19'), proc('D2950', '#19')],
      memberId: 'DD-4402-7716',
    }),
    'Checked the draft against Delta Dental’s rules',
    'added tooth #19 to the build-up, taken from the crown on the same tooth',
  ),
  // Late only after Delta Dental's usual time (about 30 days), with ELVA's status checks.
  buildClaim({
    id: 'CLM-27650',
    state: 'late',
    lateDays: 38,
    patient: 'Hana Chen',
    payer: 'Delta Dental',
    plan: 'Delta Dental PPO',
    procedures: [proc('D2740', '#14'), proc('D2950', '#14')],
    memberId: 'DD-2905-1148',
  }),
  // Denied for missing information (CARC 16): a corrected claim, not an appeal.
  buildClaim({
    id: 'CLM-28090',
    state: 'denied',
    variant: 'corrected-ready',
    patient: 'Victor Patel',
    payer: 'Aetna',
    plan: 'Aetna PPO',
    procedures: [proc('D2392', '#30'), proc('D0220', '#30')],
    memberId: 'W613905528',
  }),
  // Denied for a reason that fits an appeal (not medically necessary).
  buildClaim({
    id: 'CLM-27693',
    state: 'denied',
    variant: 'appeal-ready',
    ...(denialNecessity ? { denial: denialNecessity } : {}),
    patient: 'Aisha Nguyen',
    payer: 'Cigna',
    plan: 'Cigna DPPO',
    procedures: [proc('D3330', '#19'), proc('D0220', '#19')],
    memberId: 'U7741-0932',
  }),
  // Paid short: expected $652.00, paid $391.00, short $261.00. ELVA asked to reprocess.
  buildClaim({
    id: 'CLM-27319',
    state: 'paid-short',
    patient: 'Chloe Rossi',
    payer: 'UnitedHealthcare',
    plan: 'UnitedHealthcare PPO',
    procedures: [proc('D3330', '#3')],
    g: 815 / 1150,
    paidShort: 391,
    memberId: 'UH480915672',
  }),
  // Written off after a lost appeal: allowed $0.00 and the write-off line.
  buildClaim({
    id: 'CLM-26774',
    state: 'written-off',
    variant: 'appeal-lost',
    ...(denialFrequency ? { denial: denialFrequency } : {}),
    patient: 'Ivan Brooks',
    payer: 'Humana',
    plan: 'Humana PPO',
    procedures: [proc('D3330', '#30'), proc('D0220', '#30'), proc('D2950', '#30'), proc('D2740', '#30')],
    memberId: 'H20481936',
  }),
  // The payer pays the patient (D197): Dr. Lee is out of network, no assignment of benefits.
  // Iris Calder (CLM-28820) closes as "Paid to the patient" once Posting posts her (historyMock).
  ...PAYS_PATIENT_OPEN,
];

export const SPECIAL_IDS = new Set(SPECIAL_CLAIMS.map((c) => c.id));
